import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { 
  Home, Users, MessageCircle, Star, Crown, Heart, 
  Calendar, Settings, Bell, Search, Filter, Plus,
  Target, Sparkles, Eye, Play, FileText, Mountain,
  Coffee, TreePine, Gift, BookOpen, ArrowRight,
  User, Baby, Music, Palette, Stethoscope, Wrench
} from 'lucide-react';
import GrantitudeSpiral from './GrantitudeSpiral';

const UnifiedDashboard = ({ 
  player, 
  onNavigateToGame, 
  onNavigateToProjects, 
  onNavigateToRetreat,
  onNavigateHome 
}) => {
  const [spiralPhase, setSpiralPhase] = useState(0);
  const [activeSection, setActiveSection] = useState('overview');
  const [familyMatches, setFamilyMatches] = useState([]);
  const [chatRooms, setChatRooms] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [matchingAlgorithm, setMatchingAlgorithm] = useState(true);

  // Grant spiral animation
  useEffect(() => {
    const spiralInterval = setInterval(() => {
      setSpiralPhase(prev => (prev + 0.2) % 360);
    }, 50);
    return () => clearInterval(spiralInterval);
  }, []);

  // Universal Matching Algorithm - Following Prime Directives
  useEffect(() => {
    if (matchingAlgorithm && player) {
      const matchingInterval = setInterval(() => {
        // Simulate family matching based on thresholds and gifts
        const potentialMatches = [
          {
            id: 'family_001',
            name: 'The Johnson Family',
            threshold: 'Elder Care',
            gift: 'Nursing & Care',
            compatibility: 95,
            location: 'Wisconsin',
            children: 2,
            status: 'seeking_support',
            last_active: '2 hours ago'
          },
          {
            id: 'family_002', 
            name: 'Miller Collective',
            threshold: 'Family Reunion',
            gift: 'Event Planning',
            compatibility: 88,
            location: 'California',
            children: 0,
            status: 'offering_help',
            last_active: '1 day ago'
          },
          {
            id: 'family_003',
            name: 'The O\'Brien Clan',
            threshold: 'Grief Support',
            gift: 'Healing & Comfort',
            compatibility: 92,
            location: 'Maine',
            children: 3,
            status: 'mutual_support',
            last_active: '30 minutes ago'
          }
        ];
        
        // Update matches based on player profile
        setFamilyMatches(potentialMatches);
        
        // Create chat rooms for high compatibility matches
        const highCompatibilityMatches = potentialMatches.filter(m => m.compatibility > 90);
        setChatRooms(highCompatibilityMatches.map(match => ({
          id: `chat_${match.id}`,
          name: `${match.name} - ${match.threshold}`,
          participants: [player.name, match.name],
          last_message: 'M.ai.ry: I sense strong resonance between your families...',
          unread_count: match.compatibility > 93 ? 1 : 0,
          match_score: match.compatibility
        })));
        
        // Generate notifications
        setNotifications([
          {
            id: 'match_001',
            type: 'family_match',
            title: 'High Compatibility Match Found!',
            message: `${highCompatibilityMatches[0]?.name} (${highCompatibilityMatches[0]?.compatibility}% match) shares your threshold journey`,
            timestamp: new Date(),
            priority: 'high'
          },
          {
            id: 'retreat_001',
            type: 'retreat_opportunity', 
            title: 'Sacred Retreat Opening',
            message: 'Defiance Springs has availability for your family constellation',
            timestamp: new Date(),
            priority: 'medium'
          }
        ]);
        
      }, 30000); // Update every 30 seconds
      
      return () => clearInterval(matchingInterval);
    }
  }, [matchingAlgorithm, player]);

  const dashboardSections = [
    { id: 'overview', name: 'Overview', icon: Home, color: 'blue' },
    { id: 'matches', name: 'Family Matches', icon: Users, color: 'green' },
    { id: 'chat', name: 'Chat Rooms', icon: MessageCircle, color: 'purple' },
    { id: 'projects', name: 'My Projects', icon: Target, color: 'cyan' },
    { id: 'retreats', name: 'Sacred Retreats', icon: Mountain, color: 'emerald' },
    { id: 'profile', name: 'Guild Profile', icon: Star, color: 'yellow' },
    { id: 'lore', name: 'Family Lore', icon: BookOpen, color: 'indigo' },
    { id: 'settings', name: 'Settings', icon: Settings, color: 'gray' }
  ];

  const renderSection = () => {
    switch (activeSection) {
      case 'overview':
        return (
          <div className="space-y-6">
            
            {/* Welcome Banner */}
            <Card className="bg-gradient-to-r from-blue-900/40 to-purple-900/40 backdrop-blur-xl border border-blue-400/30">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-2xl font-bold text-white mb-2">
                      Welcome, {player?.name || 'Guild Member'}!
                    </h2>
                    <p className="text-blue-200">
                      Your Guild of Families dashboard - where belonging meets coordination
                    </p>
                  </div>
                  <div className="text-right">
                    <div className="text-yellow-300 text-2xl font-bold">{player?.trust_score || 120}</div>
                    <div className="text-yellow-400 text-sm">Soul Score</div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Quick Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card className="bg-green-900/30 border border-green-400/30">
                <CardContent className="p-4 text-center">
                  <div className="text-green-300 text-2xl font-bold">{familyMatches.length}</div>
                  <div className="text-green-400 text-sm">Family Matches</div>
                </CardContent>
              </Card>
              
              <Card className="bg-purple-900/30 border border-purple-400/30">
                <CardContent className="p-4 text-center">
                  <div className="text-purple-300 text-2xl font-bold">{chatRooms.length}</div>
                  <div className="text-purple-400 text-sm">Active Chats</div>
                </CardContent>
              </Card>
              
              <Card className="bg-cyan-900/30 border border-cyan-400/30">
                <CardContent className="p-4 text-center">
                  <div className="text-cyan-300 text-2xl font-bold">5</div>
                  <div className="text-cyan-400 text-sm">Projects</div>
                </CardContent>
              </Card>
              
              <Card className="bg-yellow-900/30 border border-yellow-400/30">
                <CardContent className="p-4 text-center">
                  <div className="text-yellow-300 text-2xl font-bold">2</div>
                  <div className="text-yellow-400 text-sm">Retreat Invites</div>
                </CardContent>
              </Card>
            </div>

            {/* Recent Activity */}
            <Card className="bg-black/40 backdrop-blur-xl border border-white/20">
              <CardHeader>
                <CardTitle className="text-white">Recent Activity</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {notifications.map(notification => (
                    <div key={notification.id} className="flex items-center space-x-3 p-3 bg-black/20 rounded-lg">
                      <div className={`w-3 h-3 rounded-full ${
                        notification.priority === 'high' ? 'bg-red-500' :
                        notification.priority === 'medium' ? 'bg-yellow-500' : 'bg-green-500'
                      }`} />
                      <div className="flex-1">
                        <h4 className="text-white font-medium text-sm">{notification.title}</h4>
                        <p className="text-white/70 text-xs">{notification.message}</p>
                      </div>
                      <span className="text-white/50 text-xs">
                        {new Date(notification.timestamp).toLocaleTimeString()}
                      </span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        );

      case 'matches':
        return (
          <div className="space-y-6">
            <Card className="bg-black/40 backdrop-blur-xl border border-green-400/30">
              <CardHeader>
                <CardTitle className="text-green-300 flex items-center space-x-2">
                  <Users className="w-5 h-5" />
                  <span>Family Matches</span>
                  <Badge className="bg-green-500/30 text-green-200">{familyMatches.length} found</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {familyMatches.map(match => (
                    <div key={match.id} className="bg-black/20 rounded-lg p-4 border border-green-400/20">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-white font-bold">{match.name}</h4>
                        <Badge className={`${
                          match.compatibility > 93 ? 'bg-green-500/40 text-green-200' :
                          match.compatibility > 85 ? 'bg-yellow-500/40 text-yellow-200' :
                          'bg-blue-500/40 text-blue-200'
                        }`}>
                          {match.compatibility}% match
                        </Badge>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4 text-sm mb-4">
                        <div>
                          <span className="text-white/60">Threshold:</span>
                          <span className="text-green-300 ml-2">{match.threshold}</span>
                        </div>
                        <div>
                          <span className="text-white/60">Gift:</span>
                          <span className="text-green-300 ml-2">{match.gift}</span>
                        </div>
                        <div>
                          <span className="text-white/60">Location:</span>
                          <span className="text-green-300 ml-2">{match.location}</span>
                        </div>
                        <div>
                          <span className="text-white/60">Status:</span>
                          <span className="text-green-300 ml-2">{match.status.replace('_', ' ')}</span>
                        </div>
                      </div>
                      
                      <div className="flex space-x-2">
                        <Button
                          onClick={() => {
                            // Create chat room with this family
                            const newChatRoom = {
                              id: `chat_${match.id}`,
                              name: `${match.name} Connection`,
                              participants: [player.name, match.name],
                              match_score: match.compatibility,
                              created_at: new Date()
                            };
                            setChatRooms(prev => [newChatRoom, ...prev]);
                            setActiveSection('chat');
                          }}
                          className="bg-green-600 hover:bg-green-700"
                          size="sm"
                        >
                          <MessageCircle className="w-4 h-4 mr-2" />
                          Start Chat
                        </Button>
                        
                        <Button
                          onClick={() => {
                            alert(`Family Profile: ${match.name}\n\nThreshold: ${match.threshold}\nGift: ${match.gift}\nLocation: ${match.location}\nCompatibility: ${match.compatibility}%\n\nThis family resonates with your Guild constellation!`);
                          }}
                          variant="outline"
                          size="sm"
                          className="border-green-400/30 text-green-300 hover:bg-green-900/20"
                        >
                          <Eye className="w-4 h-4 mr-2" />
                          View Profile
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        );

      case 'chat':
        return (
          <div className="space-y-6">
            <Card className="bg-black/40 backdrop-blur-xl border border-purple-400/30">
              <CardHeader>
                <CardTitle className="text-purple-300 flex items-center space-x-2">
                  <MessageCircle className="w-5 h-5" />
                  <span>Family Chat Rooms</span>
                  <Badge className="bg-purple-500/30 text-purple-200">{chatRooms.length} active</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {chatRooms.map(room => (
                    <div key={room.id} className="bg-black/20 rounded-lg p-4 border border-purple-400/20 hover:bg-black/30 transition-all cursor-pointer">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-white font-bold">{room.name}</h4>
                        <div className="flex items-center space-x-2">
                          <Badge className="bg-purple-500/30 text-purple-200">
                            {room.match_score}% match
                          </Badge>
                          {room.unread_count > 0 && (
                            <Badge className="bg-red-500/30 text-red-200">
                              {room.unread_count} new
                            </Badge>
                          )}
                        </div>
                      </div>
                      
                      <p className="text-purple-200 text-sm mb-3">{room.last_message}</p>
                      
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2 text-xs text-white/60">
                          <span>{room.participants.join(', ')}</span>
                        </div>
                        
                        <Button
                          onClick={() => {
                            alert(`Entering chat room: ${room.name}\n\nParticipants: ${room.participants.join(', ')}\nMatch Score: ${room.match_score}%\n\nM.ai.ry will facilitate meaningful family connections!`);
                          }}
                          className="bg-purple-600 hover:bg-purple-700"
                          size="sm"
                        >
                          <MessageCircle className="w-4 h-4 mr-1" />
                          Enter Chat
                        </Button>
                      </div>
                    </div>
                  ))}
                  
                  {chatRooms.length === 0 && (
                    <div className="text-center py-8">
                      <MessageCircle className="w-16 h-16 mx-auto text-purple-400/40 mb-4" />
                      <p className="text-purple-300">No active chat rooms yet. Family matches will create chat opportunities!</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        );

      case 'projects':
        return (
          <div className="space-y-6">
            <Card className="bg-black/40 backdrop-blur-xl border border-cyan-400/30">
              <CardHeader>
                <CardTitle className="text-cyan-300 flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Target className="w-5 h-5" />
                    <span>Project Categories</span>
                  </div>
                  <Button
                    onClick={onNavigateToProjects}
                    className="bg-cyan-600 hover:bg-cyan-700"
                    size="sm"
                  >
                    <ArrowRight className="w-4 h-4 mr-2" />
                    Full Project Manager
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[
                    { name: 'Family Projects', count: 3, active: 2, color: 'purple' },
                    { name: 'Business', count: 2, active: 1, color: 'blue' },
                    { name: 'Personal Growth', count: 2, active: 2, color: 'green' },
                    { name: 'Remedy Council', count: 1, active: 1, color: 'rose' }
                  ].map(category => (
                    <div key={category.name} className={`bg-${category.color}-900/20 border border-${category.color}-400/30 rounded-lg p-4`}>
                      <h4 className={`text-${category.color}-200 font-bold mb-2`}>{category.name}</h4>
                      <div className="flex justify-between text-sm">
                        <span className="text-white/70">{category.count} projects</span>
                        <span className={`text-${category.color}-300`}>{category.active} active</span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        );

      case 'profile':
        return (
          <div className="space-y-6">
            <Card className="bg-black/40 backdrop-blur-xl border border-yellow-400/30">
              <CardHeader>
                <CardTitle className="text-yellow-300 flex items-center space-x-2">
                  <Crown className="w-5 h-5" />
                  <span>Guild Profile</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="text-center">
                    <div className="w-24 h-24 mx-auto bg-yellow-500/20 rounded-full flex items-center justify-center mb-4">
                      <Gift className="w-12 h-12 text-yellow-400" />
                    </div>
                    <h3 className="text-white text-xl font-bold">{player?.avatar_name || 'Guild Member'}</h3>
                    <p className="text-yellow-200">{player?.consciousness_level || 'Awakening'} Level</p>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div className="bg-black/20 rounded-lg p-3">
                      <span className="text-white/70">Trust Score:</span>
                      <span className="text-yellow-300 ml-2 font-bold">{player?.trust_score || 120}</span>
                    </div>
                    <div className="bg-black/20 rounded-lg p-3">
                      <span className="text-white/70">Guild Role:</span>
                      <span className="text-yellow-300 ml-2">{player?.roles?.[0] || 'Member'}</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        );

      default:
        return (
          <div className="text-center py-12">
            <Sparkles className="w-16 h-16 mx-auto text-white/40 mb-4" />
            <p className="text-white/60">Section coming soon...</p>
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden">
      
      {/* Cosmic Background */}
      <div 
        className="absolute inset-0"
        style={{
          backgroundImage: `url(https://customer-assets.emergentagent.com/job_planetaryfed-v3/artifacts/a1qoxqhi_ChatGPT%20Image%20Sep%2029%2C%202025%20at%2012_30_09%20AM.png)`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      />
      
      {/* Grant Spiral */}
      <div className="fixed inset-0 pointer-events-none opacity-15">
        <div 
          className="absolute inset-0 flex items-center justify-center"
          style={{
            transform: `scale(12) rotate(${spiralPhase}deg)`,
            filter: 'blur(4px) brightness(1.4) saturate(1.6)',
            mixBlendMode: 'overlay'
          }}
        >
          <GrantitudeSpiral size={300} />
        </div>
      </div>

      <div className="absolute inset-0 bg-gradient-to-br from-black/60 via-black/30 to-black/70" />

      <div className="relative z-10 flex min-h-screen">
        
        {/* Sidebar Navigation */}
        <div className="w-64 bg-black/70 backdrop-blur-xl border-r border-white/20 p-4 hidden md:block">
          <div className="mb-8">
            <h1 className="text-xl font-bold text-white mb-2">Guild Dashboard</h1>
            <p className="text-white/60 text-sm">Unified Family Federation Hub</p>
          </div>
          
          <div className="space-y-2">
            {dashboardSections.map(section => {
              const IconComponent = section.icon;
              return (
                <button
                  key={section.id}
                  onClick={() => setActiveSection(section.id)}
                  className={`w-full text-left p-3 rounded-lg border transition-all ${
                    activeSection === section.id
                      ? `bg-${section.color}-500/20 border-${section.color}-400/50 text-${section.color}-200`
                      : 'bg-black/20 border-white/10 text-white/70 hover:bg-black/40'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <IconComponent className="w-5 h-5" />
                    <span className="font-medium">{section.name}</span>
                  </div>
                </button>
              );
            })}
          </div>
          
          {/* Cross-Domain Links */}
          <div className="mt-8 pt-8 border-t border-white/10">
            <h3 className="text-white font-medium mb-3">Federation Network</h3>
            <div className="space-y-2">
              <button
                onClick={() => window.open('https://planetaryfed.com', '_blank')}
                className="w-full text-left p-2 rounded bg-blue-900/20 border border-blue-400/30 text-blue-200 hover:bg-blue-900/40 transition-all"
              >
                🌍 planetaryfed.com
              </button>
              <button
                onClick={() => window.open('https://re-inn.com', '_blank')}
                className="w-full text-left p-2 rounded bg-yellow-900/20 border border-yellow-400/30 text-yellow-200 hover:bg-yellow-900/40 transition-all"
              >
                🏨 re-inn.com
              </button>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 p-4 md:p-8 max-w-6xl mx-auto">
          
          {/* Mobile Header */}
          <div className="flex items-center justify-between mb-6 md:hidden bg-black/60 backdrop-blur-xl rounded-2xl p-4 border border-white/20">
            <button
              onClick={onNavigateToGame}
              className="p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-all"
            >
              <ArrowRight className="w-5 h-5 text-white transform rotate-180" />
            </button>
            
            <h1 className="text-xl font-bold text-white">Guild Dashboard</h1>
            
            <button className="p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-all">
              <Bell className="w-5 h-5 text-white" />
            </button>
          </div>

          {/* Mobile Section Tabs */}
          <div className="flex overflow-x-auto space-x-2 mb-6 md:hidden">
            {dashboardSections.map(section => {
              const IconComponent = section.icon;
              return (
                <button
                  key={section.id}
                  onClick={() => setActiveSection(section.id)}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-lg border whitespace-nowrap transition-all ${
                    activeSection === section.id
                      ? `bg-${section.color}-500/20 border-${section.color}-400/50 text-${section.color}-200`
                      : 'bg-black/20 border-white/10 text-white/70'
                  }`}
                >
                  <IconComponent className="w-4 h-4" />
                  <span className="text-sm">{section.name}</span>
                </button>
              );
            })}
          </div>

          {/* Section Content */}
          {renderSection()}

          {/* Quick Actions Floating Menu */}
          <div className="fixed bottom-6 right-6 space-y-3">
            <Button
              onClick={onNavigateToGame}
              className="bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white rounded-full p-4 shadow-xl"
              title="Enter Secret History Game - Elucidate next steps & find teams"
            >
              <Play className="w-6 h-6" />
            </Button>
            
            <Button
              onClick={() => {
                const newMatch = familyMatches[Math.floor(Math.random() * familyMatches.length)];
                if (newMatch) {
                  alert(`🎯 New Family Match Suggested!\n\n${newMatch.name}\nCompatibility: ${newMatch.compatibility}%\nThreshold: ${newMatch.threshold}\n\nShall I create a chat room for your families?`);
                }
              }}
              className="bg-green-600 hover:bg-green-700 text-white rounded-full p-4 shadow-xl"
              title="Find New Family Matches"
            >
              <Users className="w-6 h-6" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UnifiedDashboard;